import React, { useState, useEffect } from 'react';
import Lottie from 'lottie-react';
import LazyBackgroundImage from '../../components/layout/LazyBackgroundImage';
import physioLowImage from '../../assets/animations/physio_low.jpeg';
import physioHighImage from '../../assets/animations/physio_bg.jpeg';
// Import your animation JSON files
import physioRehabAnimation from '../../assets/animations/Physio_rehab.json';
import basicMobilityAnimation from '../../assets/animations/basic_mobility.json';
import physioFlexAnimation from '../../assets/animations/Physio_flex.json';
import physioPainAnimation from '../../assets/animations/Physio_pain.json';
import physioPostAnimation from '../../assets/animations/physio_post.json';
import physioLifestyleAnimation from '../../assets/animations/physio_lifestyle.json';
// Import the images
import advScheduleImage from '../../assets/animations/adv_schedule.jpg';
import physioDoorImage from '../../assets/animations/physio_door.jpg';
import physioProgressImage from '../../assets/animations/physio_progress.jpg';
import physioHolisticImage from '../../assets/animations/physio_holistic.jpg';
import PatientReviews from '../../components/sections/PatientReviews';
import FAQSection from '../../components/sections/FAQSection';

const PhysiotherapyPage = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  // const [activeFaq, setActiveFaq] = useState(null);
  const [hoveredService, setHoveredService] = useState(null);
  const [hoveredApproach, setHoveredApproach] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    
    // Intersection Observer for slide-in animation
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setIsVisible(true);
      }
    }, { threshold: 0.1 });
    
    const approachSection = document.getElementById('approach-section');
    if (approachSection) {
      observer.observe(approachSection);
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (approachSection) {
        observer.unobserve(approachSection);
      }
    };
  }, []);

  // const toggleFaq = (index) => {
  //   setActiveFaq(activeFaq === index ? null : index);
  // };

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  // CSS Variables from the provided styles
  const cssVars = {
    primaryColor1: '#3e4b82',
    primaryColor2: '#f36437',
    primaryColor3: '#679ddd',
    secondaryColor1: '#656f9b',
    secondaryColor2: '#e1ad59',
    secondaryColor3: '#ffffff',
    accentColor1: '#ff7e67',
    accentColor2: '#7fcdbb',
    accentColor3: '#556b8d',
    fontFamilyPrimary: '"Lato", sans-serif',
    fontFamilySecondary: '"Lato", sans-serif',
    h1FontSize: isMobile ? '2.5rem' : '3.5rem',
    h1LineHeight: '1.2',
    h1MarginBottom: '0.3em',
    h1FontWeight: '800',
    pretitleFontSize: isMobile ? '1rem' : '1.2rem',
    pretitleLineHeight: '1.2',
    pretitleLetterSpacing: '0.2em',
    pretitleMarginBottom: '0.5em',
    pretitleFontWeight: '700',
    baseTextSize: isMobile ? '0.95rem' : '1rem',
    baseTextLineHeight: '1.5',
    baseTextFontWeight: '400',
    defaultTopGap: '3vmin',
    defaultBottomGap: '3vmin',
    baseTransition: '.3s',
    cBlockRadius: '15px',
    cBlockShadow: '0 4px 20px rgba(0, 0, 0, .1)',
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      color: cssVars.primaryColor1,
      fontFamily: cssVars.fontFamilyPrimary,
      background: 'linear-gradient(135deg, #f5f7fa, #e4edf5)',
      position: 'relative',
      overflow: 'hidden',
    },
    contentWrapper: {
      maxWidth: '1000px',
      margin: '0 auto',
      padding: '0 15px',
      position: 'relative',
      zIndex: 1,
    },
    hero: {
  position: 'relative',
  width: '100%',
  minHeight: isMobile ? '50vh' : '85vh', // Reduced mobile height
  height: isMobile ? '50vh' : '85vh', // Set explicit height
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: isMobile ? '0' : '0 0 30% 30% / 15px', // Remove border radius on mobile
  overflow: 'hidden',
},
    heroOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.2)',
      zIndex: 2,
    },
    heroContainer: {
      position: 'relative',
      zIndex: 3,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: isMobile ? '60vh' : '85vh',
      padding: '0 20px',
    },
    heroContent: {
      color: cssVars.secondaryColor3,
      textAlign: 'center',
      maxWidth: '800px',
      background: 'rgba(0, 0, 0, 0.3)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      borderRadius: '20px',
      padding: isMobile ? '30px 20px' : '40px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.18)',
    },
    heroPretitle: {
      fontSize: cssVars.pretitleFontSize,
      lineHeight: cssVars.pretitleLineHeight,
      letterSpacing: cssVars.pretitleLetterSpacing,
      marginBottom: cssVars.pretitleMarginBottom,
      fontWeight: cssVars.pretitleFontWeight,
      textTransform: 'uppercase',
      color: cssVars.secondaryColor3,
      textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    },
    heroTitle: {
      fontSize: cssVars.h1FontSize,
      lineHeight: cssVars.h1LineHeight,
      marginBottom: cssVars.h1MarginBottom,
      fontWeight: cssVars.h1FontWeight,
      fontFamily: cssVars.fontFamilySecondary,
      textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
    },
    tagline: {
      fontSize: isMobile ? '1rem' : '1.2rem',
      fontStyle: 'italic',
      fontWeight: '300',
      marginTop: '8px',
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
    },
    aboutSection: {
      position: 'relative',
      width: '100%',
      minHeight: isMobile ? '40vh' : '50vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(135deg, ${cssVars.primaryColor1}, ${cssVars.primaryColor3})`,
      margin: '20px 0',
      borderRadius: '15px',
      overflow: 'hidden',
    },
    aboutOverlay: {
      position: 'absolute',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.2)',
      zIndex: 1,
    },
    aboutContainer: {
      position: 'relative',
      zIndex: 2,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: isMobile ? '40vh' : '50vh',
    },
    aboutContent: {
      color: cssVars.secondaryColor3,
      textAlign: 'center',
      maxWidth: '800px',
      margin: '0 auto',
      padding: '0 20px',
    },
    aboutTitle: {
      fontSize: isMobile ? '2rem' : '2.8rem',
      lineHeight: '1.2',
      marginBottom: '20px',
      fontWeight: '700',
      fontFamily: cssVars.fontFamilySecondary,
    },
    aboutText: {
      fontSize: isMobile ? '0.95rem' : '1.1rem',
      lineHeight: '1.6',
      fontWeight: '400',
    },
    servicesSection: {
      padding: `${cssVars.defaultTopGap} 0 ${cssVars.defaultBottomGap}`,
      background: `linear-gradient(135deg, ${cssVars.accentColor1}20, ${cssVars.accentColor2}20)`,
      borderRadius: cssVars.cBlockRadius,
      margin: '15px 0',
      boxShadow: cssVars.cBlockShadow,
      position: 'relative',
      width: '100%',
      overflow: 'hidden',
    },
    sectionTitle: {
      textAlign: 'center',
      fontSize: isMobile ? '1.8rem' : '2.2rem',
      marginBottom: '20px',
      color: cssVars.primaryColor1,
      position: 'relative',
      display: 'inline-block',
      width: '100%',
      fontFamily: cssVars.fontFamilySecondary,
      fontWeight: '700',
    },
    sectionTitleAfter: {
      content: '""',
      position: 'absolute',
      bottom: '-10px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '80px',
      height: '3px',
      background: `linear-gradient(90deg, ${cssVars.primaryColor1}, ${cssVars.primaryColor3})`,
      borderRadius: '3px',
    },
    servicesGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
      gap: '30px',
      padding: '0 20px',
    },
    serviceCard: {
      background: 'rgba(255, 255, 255, 0.9)',
      borderRadius: cssVars.cBlockRadius,
      // boxShadow: cssVars.cBlockShadow,
      padding: '25px',
      transition: `transform ${cssVars.baseTransition} ease, box-shadow ${cssVars.baseTransition} ease`,
      transform: hoveredService !== null ? 'translateY(-8px)' : 'none',
      boxShadow: hoveredService !== null ? '0 12px 30px rgba(0, 0, 0, 0.15)' : '0 8px 25px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      border: '1px solid rgba(255, 255, 255, 0.8)',
      position: 'relative',
      overflow: 'hidden',
      height: '100%',
    },
    serviceCardBefore: {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '5px',
      background: `linear-gradient(90deg, ${cssVars.primaryColor1}, ${cssVars.primaryColor3})`,
    },
    serviceMedia: {
      width: '100%',
      height: '180px',
      marginBottom: '20px',
      borderRadius: '10px',
      overflow: 'hidden',
      border: '1px solid rgba(44, 123, 229, 0.1)',
    },
    serviceIcon: {
      fontSize: '2.5rem',
      marginBottom: '15px',
      textAlign: 'center',
    },
    serviceTitle: {
      fontSize: '1.3rem',
      marginBottom: '12px',
      color: cssVars.primaryColor1,
      textAlign: 'center',
      fontFamily: cssVars.fontFamilySecondary,
      fontWeight: '700',
    },
    serviceDescription: {
      fontSize: cssVars.baseTextSize,
      marginBottom: '20px',
      lineHeight: '1.5',
      textAlign: 'center',
    },
    serviceList: {
      listStyleType: 'none',
      padding: 0,
      width: '100%',
      margin: 0,
    },
    serviceListItem: {
      fontSize: cssVars.baseTextSize,
      marginBottom: '10px',
      lineHeight: '1.4',
    },
    approachSection: {
      padding: `${cssVars.defaultTopGap} 0 ${cssVars.defaultBottomGap}`,
      background: `linear-gradient(135deg, ${cssVars.accentColor3}20, ${cssVars.primaryColor3}20)`,
      borderRadius: cssVars.cBlockRadius,
      margin: '15px 0',
      boxShadow: cssVars.cBlockShadow,
      position: 'relative',
      overflow: 'hidden',
    },
    approachContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '30px',
    },
    approachCard: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: 'center',
      background: 'rgba(255, 255, 255, 0.9)',
      borderRadius: cssVars.cBlockRadius,
      overflow: 'hidden',
      boxShadow: cssVars.cBlockShadow,
      transition: `transform ${cssVars.baseTransition} ease, box-shadow ${cssVars.baseTransition} ease`,
      height: isMobile ? 'auto' : '200px',
      position: 'relative',
      opacity: 0,
    },
    approachCardEven: {
      flexDirection: isMobile ? 'column' : 'row-reverse',
    },
    approachCardLeft: {
      animation: isVisible ? 'slideInLeft 0.6s forwards' : 'none',
      animationDelay: '0.2s',
    },
    approachCardRight: {
      animation: isVisible ? 'slideInRight 0.6s forwards' : 'none',
      animationDelay: '0.4s',
    },
    approachCardBefore: {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '5px',
      height: '100%',
      background: `linear-gradient(180deg, ${cssVars.primaryColor1}, ${cssVars.primaryColor3})`,
    },
    approachImageContainer: {
      flex: isMobile ? 'none' : '0 0 300px',
      width: isMobile ? '100%' : '300px',
      height: isMobile ? '180px' : '100%',
      overflow: 'hidden',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#f0f5ff',
    },
    approachImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: `transform ${cssVars.baseTransition} ease`,
    },
    approachContent: {
      flex: '1',
      padding: isMobile ? '20px' : '30px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      height: isMobile ? 'auto' : '100%',
    },
    approachIcon: {
      fontSize: '2rem',
      marginBottom: '12px',
      color: cssVars.primaryColor1,
    },
    approachTitle: {
      fontSize: '1.3rem',
      marginBottom: '12px',
      color: cssVars.primaryColor1,
      fontFamily: cssVars.fontFamilySecondary,
      fontWeight: '700',
    },
    approachDescription: {
      fontSize: cssVars.baseTextSize,
      lineHeight: '1.5',
      color: cssVars.secondaryColor1,
    },
    faqSection: {
      padding: isMobile ? '60px 20px' : '100px 20px',
      background: '#f9fafb',
      maxWidth: '900px',
      margin: '0 auto',
    },
    faqTitle: {
      fontSize: isMobile ? '2rem' : '2.5rem',
      fontWeight: '700',
      color: cssVars.primaryColor1,
      marginBottom: '20px',
      textAlign: 'center',
    },
    faqItem: {
      background: '#fff',
      borderRadius: '10px',
      marginBottom: '1rem',
      padding: '1.2rem 1.5rem',
      boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      borderBottom: `1px solid ${cssVars.primaryColor1}20`,
    },
    faqQuestion: {
      width: '100%',
      background: 'none',
      border: 'none',
      outline: 'none',
      textAlign: 'left',
      fontSize: '1.2rem',
      fontWeight: '600',
      color: cssVars.primaryColor1,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
      marginBottom: '0.5rem',
    },
    faqIcon: {
      fontSize: '1.5rem',
      marginLeft: '10px',
      color: cssVars.primaryColor1,
    },
    faqAnswer: {
      marginTop: '10px',
      color: cssVars.secondaryColor1,
      fontSize: '1rem',
      lineHeight: '1.6',
    },
    footerSection: {
      padding: `${cssVars.defaultTopGap} 0 ${cssVars.defaultBottomGap}`,
      background: `linear-gradient(135deg, ${cssVars.primaryColor1}, ${cssVars.primaryColor3})`,
      color: cssVars.secondaryColor3,
      textAlign: 'center',
      borderRadius: '30% 30% 0 0 / 15px',
      marginTop: '20px',
      position: 'relative',
      overflow: 'hidden',
    },
    closingQuote: {
      fontSize: isMobile ? '1rem' : '1.2rem',
      fontStyle: 'italic',
      fontWeight: '300',
      maxWidth: '700px',
      margin: '0 auto',
      textShadow: '0 1px 2px rgba(0,0,0,0.1)',
    },
    decorativeElement: {
      position: 'absolute',
      width: '200px',
      height: '200px',
      borderRadius: '50%',
      background: `radial-gradient(circle, ${cssVars.primaryColor3}33, ${cssVars.secondaryColor3}00)`,
      zIndex: 0,
    },
    decorativeSpan: {
      position: 'absolute',
      borderRadius: '50%',
      background: `linear-gradient(135deg, ${cssVars.primaryColor1}, ${cssVars.primaryColor3})`,
      opacity: '0.1',
      zIndex: 0,
    },
  };

  const physio_faqs = [
    {
      question: "Who can benefit from home physiotherapy?",
      answer: "Anyone recovering from surgery, stroke, injury, or those dealing with chronic pain, mobility issues, or age-related weakness."
    },
    {
      question: "How often should I take physiotherapy sessions?",
      answer: "It depends on your condition and doctor's advice. Some may need daily sessions, while others benefit from 2–3 sessions per week."
    },
    {
      question: "Do I need special equipment at home?",
      answer: "Not always. Our physiotherapists bring basic equipment and can suggest simple aids or exercises using what you already have at home."
    },
    {
      question: "Is home physiotherapy as effective as clinic-based therapy?",
      answer: "Yes. Personalized, one-on-one therapy at home can often be more effective because the treatment is tailored to your comfort and environment."
    },
    {
      question: "Can you help with elderly physiotherapy?",
      answer: "Absolutely. We specialize in geriatric physiotherapy to improve mobility, balance, and independence in seniors."
    },
    {
      question: "Do you provide physiotherapy for neurological conditions?",
      answer: "Yes. Our therapists are experienced in stroke recovery, Parkinson's, multiple sclerosis, and other neurological disorders."
    },
    {
      question: "How long does it take to see results?",
      answer: "Recovery time varies depending on the condition, but consistent physiotherapy usually shows improvement within a few weeks."
    }
  ];

  const services = [
    {
      title: "Post-surgical rehabilitation",
      description: "Recovery after orthopedic, neurological, or cardiac surgeries.",
      features: [
        "✔ Orthopedic surgery recovery",
        "✔ Neurological rehabilitation",
        "✔ Cardiac surgery support"
      ],
      animation: physioRehabAnimation,
      animationType: 'lottie'
    },
    {
      title: "Mobility & balance training",
      description: "Support for safe walking and fall prevention.",
      features: [
        "✔ Gait training",
        "✔ Balance improvement",
        "✔ Fall prevention techniques"
      ],
      animation: basicMobilityAnimation,
      animationType: 'lottie'
    },
    {
      title: "Strengthening & flexibility exercises",
      description: "Restoring muscle strength and joint movement.",
      features: [
        "✔ Muscle strengthening",
        "✔ Joint flexibility",
        "✔ Range of motion improvement"
      ],
      animation: physioFlexAnimation,
      animationType: 'lottie'
    },
    {
      title: "Pain management",
      description: "Exercises and techniques to reduce back, neck, or joint pain.",
      features: [
        "✔ Back pain relief",
        "✔ Neck pain management",
        "✔ Joint pain reduction"
      ],
      animation: physioPainAnimation,
      animationType: 'lottie'
    },
    {
      title: "Post-injury therapy",
      description: "Rehabilitation after fractures, sprains, or sports injuries.",
      features: [
        "✔ Fracture recovery",
        "✔ Sprain rehabilitation",
        "✔ Sports injury treatment"
      ],
      animation: physioPostAnimation,
      animationType: 'lottie'
    },
    {
      title: "Lifestyle physiotherapy",
      description: "Exercises to improve posture, fitness, and overall health.",
      features: [
        "✔ Posture correction",
        "✔ Fitness improvement",
        "✔ Wellness enhancement"
      ],
      animation: physioLifestyleAnimation,
      animationType: 'lottie'
    }
  ];

  const approaches = [
    {
      icon: "fas fa-clipboard-list",
      title: "Personalized treatment plans",
      description: "Based on your doctor's advice and your goals.",
      image: advScheduleImage
    },
    {
      icon: "fas fa-home",
      title: "Care at your doorstep",
      description: "No need to travel, we bring therapy to you.",
      image: physioDoorImage
    },
    {
      icon: "fas fa-chart-line",
      title: "Progress tracking",
      description: "Regular updates and adjustments to maximize recovery.",
      image: physioProgressImage
    },
    {
      icon: "fas fa-heart",
      title: "Holistic healing",
      description: "Combining exercises with motivation and emotional encouragement.",
      image: physioHolisticImage
    }
  ];

  return (
    <div style={styles.container}>
      {/* CSS for animations */}
      <style>
        {`
          @keyframes slideInLeft {
            from {
              opacity: 0;
              transform: translateX(-50px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(50px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
        `}
      </style>
      
      {/* Decorative background elements */}
      <div style={{...styles.decorativeElement, top: '-100px', right: '-50px'}}></div>
      <div style={{...styles.decorativeElement, bottom: '20%', left: '-150px'}}></div>
      
      {/* Hero Section with Lazy Loaded Background Image */}
      <div style={styles.hero}>
        {/* Background Image Layer */}
        <LazyBackgroundImage
  lowSrc={physioLowImage}
  highSrc={physioHighImage}
  style={{
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 0,
    objectFit: 'cover', // Add this line
    backgroundPosition: 'center top', // Add this line for mobile
  }}
/>
        
        {/* Overlay Layer */}
        <div style={styles.heroOverlay}></div>
        
        {/* Content Layer */}
        <div style={styles.heroContainer}>
          <div style={styles.heroContent}>
            <div style={styles.heroPretitle}>Expert Rehabilitation Services</div>
            <h1 style={styles.heroTitle}>Physiotherapy</h1>
            <p style={styles.tagline}>"Regain strength, mobility, and confidence—one step at a time."</p>
          </div>
        </div>
      </div>
      
      {/* About Section - Similar to Hero Section */}
      <div style={styles.aboutSection}>
        <div style={styles.aboutOverlay}></div>
        <div style={styles.aboutContainer}>
          <div style={styles.aboutContent}>
            <h2 style={styles.aboutTitle}>Personalized Physiotherapy at Home</h2>
            <p style={styles.aboutText}>
              At Timeless Home Care, our Physiotherapy service helps individuals recover from illness, injury, 
              or surgery and regain independence in the comfort of their own home. Our trained physiotherapists design 
              tailored exercise programs to improve mobility, strength, and overall wellbeing—helping you live 
              pain-free and active again.
            </p>
          </div>
        </div>
      </div>
      
      {/* Services Section - Full Width */}
      <div style={styles.servicesSection}>
        <div style={styles.contentWrapper}>
          <h2 style={styles.sectionTitle}>What We Offer in Physiotherapy Care</h2>
          
          <div style={styles.servicesGrid}>
            {services.map((service, index) => (
              <div 
                key={index}
                style={styles.serviceCard}
                onMouseEnter={() => setHoveredService(index)}
                onMouseLeave={() => setHoveredService(null)}
              >
                {/* Decorative top border */}
                <div style={styles.serviceCardBefore}></div>
                
                {/* Animation or Image */}
                <div style={styles.serviceMedia}>
                  {service.animationType === 'lottie' ? (
                    <Lottie 
                      animationData={service.animation} 
                      loop={true}
                      autoplay={true}
                      style={{ width: '100%', height: '100%' }}
                    />
                  ) : (
                    <img 
                      src={service.image} 
                      alt={service.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  )}
                </div>
                
                <div style={styles.serviceIcon}>{service.icon}</div>
                <h3 style={styles.serviceTitle}>{service.title}</h3>
                <p style={styles.serviceDescription}>{service.description}</p>
                <ul style={styles.serviceList}>
                  {service.features.map((feature, i) => (
                    <li key={i} style={styles.serviceListItem}>{feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        
        {/* Decorative spans in services section */}
        <div style={{...styles.decorativeSpan, width: '120px', height: '120px', top: '-30px', right: '10%'}}></div>
        <div style={{...styles.decorativeSpan, width: '80px', height: '80px', bottom: '-20px', left: '5%'}}></div>
      </div>
      
      {/* Approach Section - Slide from Left and Right Alternately */}
      <div id="approach-section" style={styles.approachSection}>
        <div style={styles.contentWrapper}>
          <h2 style={styles.sectionTitle}>Our Approach to Physiotherapy</h2>
          <div style={styles.approachContainer}>
            {approaches.map((approach, index) => (
              <div 
                key={index}
                style={{
                  ...styles.approachCard,
                  // Apply layout class based on index (even = normal, odd = reversed)
                  ...(index % 2 === 1 ? styles.approachCardEven : {}),
                  // Apply animation class based on index (even = left, odd = right)
                  ...(index % 2 === 0 ? styles.approachCardLeft : styles.approachCardRight),
                  transform: hoveredApproach === index ? 'translateY(-5px)' : 'none',
                  boxShadow: hoveredApproach === index ? '0 12px 30px rgba(0, 0, 0, 0.15)' : '0 8px 25px rgba(0, 0, 0, 0.1)',
                }}
                onMouseEnter={() => setHoveredApproach(index)}
                onMouseLeave={() => setHoveredApproach(null)}
              >
                {/* Decorative left border */}
                <div style={styles.approachCardBefore}></div>
                
                <div style={styles.approachImageContainer}>
                  <img 
                    src={approach.image} 
                    alt={approach.title}
                    style={{
                      ...styles.approachImage,
                      transform: hoveredApproach === index ? 'scale(1.05)' : 'scale(1)',
                    }}
                  />
                </div>
                <div style={styles.approachContent}>
                  <div style={styles.approachIcon}>
                    <i className={approach.icon}></i>
                  </div>
                  <h3 style={styles.approachTitle}>{approach.title}</h3>
                  <p style={styles.approachDescription}>{approach.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Decorative spans in approach section */}
        <div style={{...styles.decorativeSpan, width: '150px', height: '150px', top: '-40px', left: '8%'}}></div>
        <div style={{...styles.decorativeSpan, width: '100px', height: '100px', bottom: '-30px', right: '12%'}}></div>
      </div>
      
      <FAQSection 
        faqs={physio_faqs}
      
      />
      
      <div style={styles.footerSection}>
        {/* Decorative spans in footer section */}
        <div style={{...styles.decorativeSpan, width: '180px', height: '180px', top: '-50px', right: '10%'}}></div>
        <div style={{...styles.decorativeSpan, width: '120px', height: '120px', bottom: '-40px', left: '15%'}}></div>
        
        <div style={styles.contentWrapper}>
          <p style={styles.closingQuote}>"Start your recovery journey today with expert physiotherapy at home."</p>
        </div>
      </div>
      <PatientReviews />
    </div>
  );
};

export default PhysiotherapyPage;